import { Box, Grid } from "@chakra-ui/react"
import { EntityId } from "@reduxjs/toolkit"
import { useVirtualizer } from "@tanstack/react-virtual"
import React, { useEffect, useMemo, useRef, useState } from "react"

export interface CloudImageCellProps {
  id: EntityId
  isSelected: boolean
  onImageSelect: (id: EntityId) => void
}

interface Props {
  imageIds: EntityId[]
  onImageSelect: (image: EntityId) => void
  selectedImageIds: EntityId[]
  imageComponent: React.ComponentType<CloudImageCellProps>
  onEndReached?: () => void
}

// Layout constants. We own the column math (see below) rather than letting a
// virtualization library infer it from a measured tile — that inference, on a
// responsive `1fr` grid, is what caused tiles to jump on window resize.
const MIN_TILE = 165 // min tile width; matches the cell height in VNZImageSelector
const GAP = 8 // px gutter, both axes
const ROW_HEIGHT = MIN_TILE + GAP // fixed row pitch (tile + vertical gutter)

// Track the scroll container's content width via ResizeObserver so the column
// count reacts to resizes. SSR-safe (Gatsby): only runs on the client.
const useElementWidth = (ref: React.RefObject<HTMLElement>) => {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(entries => {
      setWidth(entries[0]?.contentRect.width ?? 0)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return width
}

const CloudImageBrowser: React.FC<Props> = ({
  imageIds,
  onImageSelect,
  selectedImageIds,
  imageComponent: ImageComponent,
  onEndReached,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const width = useElementWidth(scrollRef)

  // Columns are computed deterministically from the measured width, so they
  // always match what we render — no library guess to drift out of sync.
  const columns = Math.max(1, Math.floor((width + GAP) / (MIN_TILE + GAP)))
  const rowCount = Math.ceil(imageIds.length / columns)

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 1,
  })

  // Infinite scroll: notify the consumer when the last row is rendered.
  // Effect-based rather than scroll-based so it also fires when a page doesn't
  // overflow the viewport (pages then auto-chain until content is scrollable).
  const virtualItems = rowVirtualizer.getVirtualItems()
  const lastVisibleRowIndex = virtualItems.length
    ? virtualItems[virtualItems.length - 1].index
    : -1

  useEffect(() => {
    if (
      onEndReached &&
      lastVisibleRowIndex >= 0 &&
      lastVisibleRowIndex >= rowCount - 1
    ) {
      onEndReached()
    }
  }, [lastVisibleRowIndex, rowCount, onEndReached])

  // O(1) selection lookup per cell instead of Array.find inside the render loop
  const selectedSet = useMemo(
    () => new Set(selectedImageIds),
    [selectedImageIds]
  )

  return (
    <Box ref={scrollRef} height="100%" width="100%" overflowY="auto">
      <Box
        position="relative"
        width="100%"
        height={`${rowVirtualizer.getTotalSize()}px`}
      >
        {virtualItems.map(virtualRow => {
          const start = virtualRow.index * columns
          const rowIds = imageIds.slice(start, start + columns)

          return (
            <Grid
              key={virtualRow.key}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              transform={`translateY(${virtualRow.start}px)`}
              templateColumns={`repeat(${columns}, minmax(0, 1fr))`}
              gap={`${GAP}px`}
            >
              {rowIds.map(id => (
                <ImageComponent
                  key={id}
                  id={id}
                  isSelected={selectedSet.has(id)}
                  onImageSelect={onImageSelect}
                />
              ))}
            </Grid>
          )
        })}
      </Box>
    </Box>
  )
}

export default CloudImageBrowser
